import { useCallback, useRef } from 'react';
import type { ImportProviderConfig } from '@/store/publicUploadApi';

declare global {
  interface Window {
    gapi: {
      load: (api: string, callback: () => void) => void;
      client: {
        load: (api: string, version: string) => Promise<void>;
      };
    };
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token: string; error?: string }) => void;
          }) => {
            requestAccessToken: (opts?: { prompt?: string }) => void;
          };
        };
      };
      picker: {
        PickerBuilder: new () => GooglePickerBuilder;
        ViewId: { DOCS: string; FOLDERS: string };
        DocsView: new (viewId?: string) => GoogleDocsView;
        Action: { PICKED: string; CANCEL: string };
        Feature: { MULTISELECT_ENABLED: string };
      };
    };
  }
}

interface GoogleDocsView {
  setSelectFolderEnabled: (enabled: boolean) => GoogleDocsView;
  setMimeTypes: (mimeTypes: string) => GoogleDocsView;
}

interface GooglePickerBuilder {
  addView: (view: unknown) => GooglePickerBuilder;
  enableFeature: (feature: string) => GooglePickerBuilder;
  setOAuthToken: (token: string) => GooglePickerBuilder;
  setDeveloperKey: (key: string) => GooglePickerBuilder;
  setAppId: (appId: string) => GooglePickerBuilder;
  setCallback: (callback: (data: GooglePickerCallbackData) => void) => GooglePickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
}

interface GooglePickerCallbackData {
  action: string;
  docs?: Array<{
    id: string;
    name: string;
    mimeType: string;
  }>;
}

export interface GooglePickerResult {
  fileIds: string[];
  folderIds: string[];
  accessToken: string;
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Google Picker hook for the public upload page.
 * Accepts config as a parameter instead of fetching from the authenticated API.
 */
export function usePublicGooglePicker(config: ImportProviderConfig | null) {
  const accessTokenRef = useRef<string>('');

  const openPicker = useCallback(async (): Promise<GooglePickerResult | null> => {
    if (!config?.google.clientId) {
      throw new Error('Google picker not configured');
    }

    await loadScript('https://accounts.google.com/gsi/client', 'google-gsi');
    await loadScript('https://apis.google.com/js/api.js', 'google-api');

    return new Promise((resolve, reject) => {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: config.google.clientId,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (tokenResponse) => {
          if (tokenResponse.error) {
            reject(new Error(tokenResponse.error));
            return;
          }

          accessTokenRef.current = tokenResponse.access_token;

          window.gapi.load('picker', () => {
            const picker = window.google.picker;

            const docsView = new picker.DocsView(picker.ViewId.DOCS);
            docsView.setSelectFolderEnabled(true);

            const folderView = new picker.DocsView(picker.ViewId.FOLDERS);
            folderView.setSelectFolderEnabled(true);

            const view = new (picker.PickerBuilder as unknown as new () => GooglePickerBuilder)()
              .addView(docsView)
              .addView(folderView)
              .enableFeature(picker.Feature.MULTISELECT_ENABLED)
              .setOAuthToken(accessTokenRef.current)
              .setDeveloperKey(config.google.apiKey)
              .setAppId(config.google.appId)
              .setCallback((data: GooglePickerCallbackData) => {
                if (data.action === picker.Action.PICKED && data.docs) {
                  const FOLDER_MIME = 'application/vnd.google-apps.folder';
                  resolve({
                    fileIds: data.docs.filter((d) => d.mimeType !== FOLDER_MIME).map((d) => d.id),
                    folderIds: data.docs.filter((d) => d.mimeType === FOLDER_MIME).map((d) => d.id),
                    accessToken: accessTokenRef.current,
                  });
                } else if (data.action === picker.Action.CANCEL) {
                  resolve(null);
                }
              })
              .build();

            view.setVisible(true);
          });
        },
      });

      tokenClient.requestAccessToken({ prompt: '' });
    });
  }, [config]);

  return { openPicker, isConfigured: !!config?.google.clientId };
}
